import axios from 'axios';

import { BASE_URL } from '@/constants/constant';

// The CRM is served from the same host as the core API, under /app/crm (UI) and
// /app/crm/api/v1 (API). It verifies the same token the admin panel signs in with.
const LOCAL_OVERRIDE_KEY = 'nitex:crm-base-url';

const isLocalhost = () => ['localhost', '127.0.0.1'].includes(window.location.hostname);

export const getCrmBaseUrl = () => {
	// Local development can point at a locally running CRM (e.g. http://localhost:3001).
	if (isLocalhost()) {
		const override = localStorage.getItem(LOCAL_OVERRIDE_KEY);
		if (override) return override.replace(/\/$/, '');
	}
	return `${BASE_URL.replace(/\/api(?:\/core)?\/?$/, '')}/app/crm`;
};

export const crmWebUrl = (path) => `${getCrmBaseUrl()}${path}`;

const crmAxios = axios.create({
	headers: { 'Content-Type': 'application/json', Accept: 'application/json' }
});

// This instance skips the global axios interceptors, so auth is added here.
crmAxios.interceptors.request.use((config) => {
	const token = localStorage.getItem('token');
	config.baseURL = `${getCrmBaseUrl()}/api/v1`;
	if (token) {
		config.headers.Authorization = /^Bearer\s/i.test(token.trim())
			? token.trim()
			: `Bearer ${token.trim()}`;
	}
	return config;
});

// Responses are wrapped as { data, error, message }.
const unwrap = async (request) => {
	try {
		const response = await request;
		return response.data?.data ?? null;
	} catch (error) {
		const apiError = error?.response?.data?.error;
		const message =
			(typeof apiError === 'string' ? apiError : apiError?.message) ||
			error?.message ||
			'CRM request failed';
		throw new Error(message);
	}
};

export const crmApi = {
	/** Null until the user has connected Outlook. */
	mailAccount: () => unwrap(crmAxios.get('/mail/accounts/me')),
	mailFolders: () => unwrap(crmAxios.get('/mail/folders')),
	mailThreads: ({ wellKnownName = 'inbox', pageSize = 40 } = {}) =>
		unwrap(crmAxios.get('/mail/threads', { params: { wellKnownName, page: 1, pageSize } })),
	calendarEvents: ({ from, to, timezone }) =>
		unwrap(crmAxios.get('/calendar/events', { params: { from, to, timezone } }))
};
